// File: client/src/variants/chaos/useChaosRuntime.js
//
// Chaos Chess runtime — the configurable sandbox. Players toggle independent
// subsystems; each enabled toggle contributes a small, conflict-free middleware that
// the platform composeMiddleware helper stacks. No toggle fights another over the
// same mechanic (the deliberate design choice over merging whole variant runtimes).
//
// Toggles (from config.settings, defaults in CHAOS_DEFAULTS):
//   randomEvents — a random board modifier fires every `eventEvery` full turns
//   gravity      — gravity fires every `gravityEvery` full turns
//   meteor       — a meteor fires every `meteorEvery` full turns
//   shrink       — the board shrinks like Chess Royale (with last-king-alive victory)
//
// All board mutations happen in afterMove on the committed board. Shrink reuses the
// pure Royale shrink module; events reuse the shared modifier catalog. A leading
// "counting" contribution increments the move counter exactly once per move, so the
// later checks read an unambiguous post-increment value.

import React, { useRef, useState, useCallback } from 'react';
import { Settings2 } from 'lucide-react';
import { createPRNG } from '../../platform/prng';
import { composeMiddleware } from '../../platform/composeMiddleware';
import { BOARD_MODIFIERS, gravity as gravityMod, meteor as meteorMod, pickModifier } from '../../platform/modifiers';
import { shrinkLevel, playableSize, isPlayable, applyShrink, kingElimination } from '../royale/shrink';

export const CHAOS_DEFAULTS = {
  randomEvents: true,
  eventEvery: 3,
  gravity: false,
  gravityEvery: 4,
  meteor: false,
  meteorEvery: 5,
  shrink: false,
};

export function useChaosRuntime({ config, resumePayload }) {
  const settings = { ...CHAOS_DEFAULTS, ...(config.settings || {}) };
  const seedRef = useRef(resumePayload?.seed ?? Math.floor(Math.random() * 1e9));
  const rngRef = useRef(createPRNG(seedRef.current));
  const moveCountRef = useRef(resumePayload?.moveCount || 0);
  const shrinkLevelRef = useRef(resumePayload?.shrinkLevel || 0);
  const [message, setMessage] = useState(null);

  const fullTurns = () => Math.floor(moveCountRef.current / 2);

  // Build the enabled toggle contributions. `counting` runs first so period checks
  // read the incremented value directly.
  const counting = { afterMove() { moveCountRef.current += 1; } };
  const checks = [];

  if (settings.shrink) {
    checks.push({
      transformLegalMoves(gameState, from, moves) {
        return moves.filter(m => isPlayable(m.row, m.col, fullTurns()));
      },
      afterMove(newState) {
        const lvl = shrinkLevel(fullTurns());
        if (lvl > shrinkLevelRef.current) {
          shrinkLevelRef.current = lvl;
          applyShrink(newState.board, fullTurns());
        }
      },
    });
  }
  if (settings.randomEvents) {
    checks.push({
      afterMove(newState) {
        if (moveCountRef.current % (settings.eventEvery * 2) === 0) {
          const mod = pickModifier(rngRef.current, BOARD_MODIFIERS);
          setMessage(mod.apply(newState.board, rngRef.current, {}));
        }
      },
    });
  }
  if (settings.gravity) {
    checks.push({
      afterMove(newState) {
        if (moveCountRef.current % (settings.gravityEvery * 2) === 0) {
          gravityMod.apply(newState.board);
          setMessage('Gravity pulled every piece down.');
        }
      },
    });
  }
  if (settings.meteor) {
    checks.push({
      afterMove(newState) {
        if (moveCountRef.current % (settings.meteorEvery * 2) === 0) {
          setMessage(meteorMod.apply(newState.board, rngRef.current));
        }
      },
    });
  }

  const composed = composeMiddleware([counting, ...checks]);

  // With board shrink enabled, a king can be eliminated by the collapsing ring.
  const getVictory = useCallback((gameState, defaultStatus) => {
    if (settings.shrink) {
      const elim = kingElimination(gameState.board);
      if (elim) return elim;
    }
    return defaultStatus && defaultStatus.over ? defaultStatus : null;
    // eslint-disable-next-line
  }, [settings.shrink]);

  const getPersistencePayload = useCallback(() => ({
    seed: seedRef.current,
    moveCount: moveCountRef.current,
    shrinkLevel: shrinkLevelRef.current,
  }), []);

  const enabled = Object.keys(CHAOS_DEFAULTS).filter(k => settings[k] === true);

  const renderOverlay = (slot) => {
    if (slot !== 'top') return null;
    return (
      <div className="flex flex-col items-center gap-1 my-2">
        <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-indigo-700/40 border-indigo-500/50 text-indigo-200">
          <Settings2 size={14} /> Chaos: {enabled.length ? enabled.join(', ') : 'standard chess'}
          {settings.shrink && ` · arena ${playableSize(fullTurns())}×${playableSize(fullTurns())}`}
        </span>
        {message && <span className="text-[11px] text-amber-300">⚡ {message}</span>}
      </div>
    );
  };

  return { moveMiddleware: composed, getVictory, getPersistencePayload, renderOverlay };
}
